import dayjs from 'dayjs';
import React from 'react';
import {Box, styled} from '@mui/material';
import {Body, designSystemToken, Icon} from '@lightricks/react-design-system';
import {getBundleTranslatedName} from '@contentlab/views/contentlab/pricing/utils';
import translate from '@/utils/translate';
import LEGACY_COLORS from '@/config/legacyColors';
import ContentLabPlanBundle from '@/types/models/pricing/contentLabPlanBundle';
import Divider from '@/components/divider';
import Skeleton from '@/components/skeleton';

export type TooltipContentProps = {
  isLoading: boolean;
  tokensBalance?: string;
  tokensRenewalDate?: Date | null;
  tokensRenewalAmount?: number;
  plan?: ContentLabPlanBundle;
  handleSeeAllPlansClick?: () => void;
  isAutoRenewEnabled?: boolean;
};

const TooltipContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '239px',
  justifyContent: 'center',
  alignItems: 'flex-start',
  gap: '12px',
  padding: '2px 0',
}));

const TokensContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '4px',
  alignSelf: 'stretch',
}));

const TokensTitle = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
}));

const ClickableSpan = styled('span')(() => ({
  color: designSystemToken('semantic.fg.brand'),
  cursor: 'pointer',
}));

const PlanDescription = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '2px',
}));

function TooltipContentSkeleton() {
  return (
    <TooltipContainer>
      <Skeleton variant="rounded" borderRadius="4px" width={182} height={16} />
      <Skeleton variant="rounded" borderRadius="4px" width={144} height={16} />
      <Skeleton variant="rounded" borderRadius="4px" width={92} height={16} />
    </TooltipContainer>
  );
}

export default function TooltipContent(props: TooltipContentProps) {
  const {
    isLoading,
    tokensBalance,
    tokensRenewalDate,
    tokensRenewalAmount,
    plan,
    handleSeeAllPlansClick,
    isAutoRenewEnabled,
  } = props;

  if (isLoading) {
    return <TooltipContentSkeleton />;
  }

  const planName = plan ? getBundleTranslatedName(plan) : '';
  const isFreeTrial = plan === ContentLabPlanBundle.TRIAL;
  const isCancelledFreeTrial = isFreeTrial && isAutoRenewEnabled === false;

  const getClickableText = (currentPlan: ContentLabPlanBundle | undefined) => {
    if (isCancelledFreeTrial) {
      return 'Upgrade now';
    }
    const planTextMap: Partial<Record<ContentLabPlanBundle, string>> = {
      [ContentLabPlanBundle.FREE]: 'See all pricing plans',
      [ContentLabPlanBundle.TRIAL]: 'Start Full Plan Now',
    };
    if (!currentPlan) {
      return 'Upgrade now';
    }
    return planTextMap[currentPlan] || 'Upgrade now';
  };

  const daysUntilRenewal = tokensRenewalDate
    ? dayjs(tokensRenewalDate).diff(dayjs(), 'day')
    : null;

  const daysUntilRenewalLocaleKey = (() => {
    if (isCancelledFreeTrial) {
      return 'Free-trial-days-left';
    }
    if (isFreeTrial) {
      return 'Plan-starts-in-days';
    }
    return 'Renews-in-days';
  })();

  const getTokensTitleText = () => {
    if (isFreeTrial) {
      return translate('free credits left', {credits: tokensBalance});
    }
    if (tokensBalance === '0') {
      return translate('Out of credits');
    }
    return translate('Credits left', {credits: tokensBalance});
  };

  return (
    <TooltipContainer>
      <TokensContainer>
        <TokensTitle>
          <Icon
            name={isFreeTrial ? 'Features-Gift-Before' : 'Features-Flash-Fill'}
            size="small"
            appearance="primary"
            color={designSystemToken('semantic.fg.brand')}
          />
          <Body size="lg">{getTokensTitleText()}</Body>
        </TokensTitle>
        <Box>
          <Body size="sm" color={designSystemToken('semantic.fg.secondary')}>
            {daysUntilRenewal !== null &&
              translate(daysUntilRenewalLocaleKey, {days: daysUntilRenewal})}
            {daysUntilRenewal !== null && ' '}
            <ClickableSpan
              onClick={(event: any) => {
                event.stopPropagation();
                handleSeeAllPlansClick?.();
              }}
            >
              {translate(getClickableText(plan))}
            </ClickableSpan>
          </Body>
        </Box>
      </TokensContainer>
      <Divider margin={1} />
      <PlanDescription>
        <Body
          size="sm"
          // Only used for bold text inside the translation, it's not really dangerous
        >
          <span
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: translate(
                isFreeTrial
                  ? 'free-trial-renewal-description'
                  : 'tokens-renewal-description',
                {
                  plan: planName,
                  tokensRenewalAmount,
                  ...(isFreeTrial && {days: daysUntilRenewal}),
                }
              ),
            }}
          />
          {isCancelledFreeTrial && (
            <span style={{color: designSystemToken('semantic.fg.secondary')}}>
              {' '}
              {translate('plan cancelled')}
            </span>
          )}
        </Body>
      </PlanDescription>
    </TooltipContainer>
  );
}
